import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    Grid,
    Alert,
    CircularProgress,
    Typography,
    Button,
    Divider,
    Stack,
    Link as MuiLink,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import DrawerScaffold from "../../app/components/scaffold/DrawerScaffold";
import MainDrawerContent from "app/components/drawer/MainDrawerContent";
import withAppVersion, { WithAppVersionProps } from "features/appVersion/withAppVersion";

import { pb } from './../../utils/pocketbase';
import type { WikiRecord } from "./../../utils/types";

const COLLECTION = "wiki";

const VorsorgeWikiDetail: React.FC<WithAppVersionProps> = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

    const [item, setItem] = React.useState<WikiRecord | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let alive = true;

        (async () => {
            if (!id) {
                setError("Kein Wiki-ID Parameter vorhanden.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const rec = await pb.collection(COLLECTION).getOne<WikiRecord>(id, {
                    filter: "enabled = true",
                    query: { _ts: Date.now() }, // cache-buster
                });

                if (alive) setItem(rec);
            } catch (e: any) {
                if (alive) setError(e?.message ?? "Fehler beim Laden des Wiki-Eintrags.");
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [id]);

    const fileUrl = React.useCallback(
        (record: WikiRecord, filename: string) => {
            // PocketBase: URL für Datei in record/files
            return pb.files.getUrl(record as any, filename);
        },
        []
    );

    return (
        <DrawerScaffold
            title={"Vorsorge Wiki"}
            actions={<></>}
            drawerContent={<MainDrawerContent />}
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()} sx={{ mb: 2 }}>
                        Zurück
                    </Button>

                    {loading && <CircularProgress size={24} />}

                    {error && <Alert severity="error">{error}</Alert>}

                    {!loading && !error && !item && (
                        <Alert severity="warning">Eintrag nicht gefunden.</Alert>
                    )}

                    {!loading && !error && item && (
                        <Stack spacing={2}>
                            <Typography variant="h4">{item.title}</Typography>

                            <Divider />

                            {/* Content als HTML */}
                            <div
                                dangerouslySetInnerHTML={{ __html: item.content ?? "" }}
                            />

                            <Divider />
                            {
                                (item.files && item.files.length > 0) &&
                                <div>
                                    <Typography variant="h6">Dateien</Typography>
                                    {(!item.files || item.files.length === 0) ? (
                                        <Alert severity="info">Keine Dateien vorhanden.</Alert>
                                    ) : (
                                        <Stack spacing={1}>
                                            {item.files.map((fn) => (
                                                <MuiLink
                                                    key={fn}
                                                    href={fileUrl(item, fn)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {fn}
                                                </MuiLink>
                                            ))}
                                        </Stack>
                                    )}
                                </div>
                            }
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </DrawerScaffold>
    );
};

export default withAppVersion(VorsorgeWikiDetail);
